import { lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { Erro403Page } from '@/app/erros/Erro403Page';
import { Erro404Page } from '@/app/erros/Erro404Page';
import { AppLayout } from '@/app/layout/AppLayout';
import { PrivateRoute } from '@/app/PrivateRoute';
import { Providers } from '@/app/providers';
import { RotaComPermissao } from '@/app/RotaComPermissao';
import { AmbientesPage } from '@/features/admin/ambientes/pages/AmbientesPage';
import { AlertasPage } from '@/features/admin/alertas/pages/AlertasPage';
import { AuditoriaPage } from '@/features/admin/auditoria/pages/AuditoriaPage';
import { RelatoriosPage } from '@/features/admin/relatorios/pages/RelatoriosPage';
import { CargosPage } from '@/features/admin/cargos/pages/CargosPage';
import { CatalogoExamesPage } from '@/features/admin/catalogo-exames/pages/CatalogoExamesPage';
import { PendenciasLaboratorioPage } from '@/features/admin/catalogo-exames/pages/PendenciasLaboratorioPage';
import { ExameDetalhePage } from '@/features/admin/catalogo-exames/pages/ExameDetalhePage';
import { CatalogoDiagnosticosPage } from '@/features/admin/catalogo-diagnosticos/pages/CatalogoDiagnosticosPage';
import { CatalogoFarmacosPage } from '@/features/admin/catalogo-farmacos/pages/CatalogoFarmacosPage';
import { FarmacoDetalhePage } from '@/features/admin/catalogo-farmacos/pages/FarmacoDetalhePage';
import { CuponsPage } from '@/features/admin/cupons/pages/CuponsPage';
import { AprovacaoMateriaisPage } from '@/features/admin/materiais/pages/AprovacaoMateriaisPage';
import { MetricasParceirosPage } from '@/features/admin/metricas/pages/MetricasParceirosPage';
import { ParceirosAdminPage } from '@/features/admin/parceiros/pages/ParceirosAdminPage';
import { ProdutosParceirosPage } from '@/features/admin/produtos-parceiros/pages/ProdutosParceirosPage';
import { UnidadesPage } from '@/features/admin/unidades/pages/UnidadesPage';
import { UsuariosPage } from '@/features/admin/usuarios/pages/UsuariosPage';
import { EntrarPeloAppPage } from '@/features/auth/pages/EntrarPeloAppPage';
import { EsqueciSenhaPage } from '@/features/auth/pages/EsqueciSenhaPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RedefinirSenhaPage } from '@/features/auth/pages/RedefinirSenhaPage';
import { RegistroPage } from '@/features/auth/pages/RegistroPage';
import { VerificarEmailPage } from '@/features/auth/pages/VerificarEmailPage';
import { DocumentoCompartilhadoPage } from '@/features/documentos/pages/DocumentoCompartilhadoPage';
import { VerificarReceitaPage } from '@/features/documentos/pages/VerificarReceitaPage';
import { AceitarConvitePage } from '@/features/administracao/pages/AceitarConvitePage';
import { EquipePage } from '@/features/administracao/pages/EquipePage';
import { PoliticaDePrivacidadePage } from '@/features/legal/pages/PoliticaDePrivacidadePage';
import { TermosDeUsoPage } from '@/features/legal/pages/TermosDeUsoPage';
import { AtendimentosPage } from '@/features/assessoria/pages/AtendimentosPage';
import { MeusArtigosPage } from '@/features/assessoria/pages/MeusArtigosPage';
import { ClubePage } from '@/features/clube/pages/ClubePage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { EspecialidadesDiscussaoPage } from '@/features/admin/especialidades-discussao/pages/EspecialidadesDiscussaoPage';
import { AdvisorsPage } from '@/features/admin/advisors/pages/AdvisorsPage';
import { ChamadoAtendimentoPage } from '@/features/suporte/pages/ChamadoAtendimentoPage';
import { ChamadoDetalhePage } from '@/features/suporte/pages/ChamadoDetalhePage';
import { FilaDeChamadosPage } from '@/features/suporte/pages/FilaDeChamadosPage';
import { MeusChamadosPage } from '@/features/suporte/pages/MeusChamadosPage';
import { PrecosDiscussaoPage } from '@/features/admin/precos-discussao/pages/PrecosDiscussaoPage';
import { DiscussaoDetalhePage } from '@/features/discussoes/pages/DiscussaoDetalhePage';
import { DiscussoesPage } from '@/features/discussoes/pages/DiscussoesPage';
import { NovaDiscussaoPage } from '@/features/discussoes/pages/NovaDiscussaoPage';
import { LaudoDoExamePage } from '@/features/exames/pages/LaudoDoExamePage';
import { ExamesPage } from '@/features/exames/pages/ExamesPage';
import { HistoricoPage } from '@/features/historico/pages/HistoricoPage';
import { LandingPage } from '@/features/landing/pages/LandingPage';
import { PacientesPage } from '@/features/pacientes/pages/PacientesPage';
import { ParceiroDetalhePage } from '@/features/parceiros/pages/ParceiroDetalhePage';
import { ParceirosPage } from '@/features/parceiros/pages/ParceirosPage';
import { MeusMateriaisPage } from '@/features/portal-parceiro/pages/MeusMateriaisPage';
import { PerfilPage } from '@/features/perfil/pages/PerfilPage';
import { SerproIdRetornoPage } from '@/features/perfil/pages/SerproIdRetornoPage';
import { ReceituariosPage } from '@/features/receituarios/pages/ReceituariosPage';
import { RequisicoesPage } from '@/features/requisicoes/pages/RequisicoesPage';
import { TutoresPage } from '@/features/tutores/pages/TutoresPage';
import { ROTAS } from '@/shared/lib/rotas';

/**
 * ⚠️ **A ÚNICA rota adiada do app, e é de propósito.**
 *
 * O `recharts` custa ~90 KB gzip — mais que todo o resto desta tela, e nenhuma
 * outra o usa. No bundle principal, toda visita ao login pagaria por um gráfico
 * que só a Análise Comparativa desenha. O `<Suspense>` que a cobre está no
 * `AppLayout`, em volta do `<Outlet />`.
 *
 * `.then()` mapeando para `default` em vez de trocar para export default: o
 * `React.lazy` exige default, e o repo exporta nomeado em toda parte —
 * inclusive nos testes, que importam o componente direto, sem passar por aqui.
 */
const AnaliseComparativaPage = lazy(() =>
  import('@/features/analises/pages/AnaliseComparativaPage').then((modulo) => ({
    default: modulo.AnaliseComparativaPage,
  })),
);

/**
 * Adiada pelo MESMO motivo: a ficha clínica desenha a curva de peso com
 * `recharts`.
 *
 * Ela e a Análise Comparativa são as duas únicas telas que o usam, e o bundle
 * do gráfico é compartilhado entre os dois chunks — quem nunca abre nenhuma das
 * duas não paga por ele.
 */
const FichaClinicaPage = lazy(() =>
  import('@/features/pacientes/pages/FichaClinicaPage').then((modulo) => ({
    default: modulo.FichaClinicaPage,
  })),
);

/**
 * Uma feature = uma entrada no router (regra 4 do ESTRUTURA-PASTAS §2).
 *
 * `Providers` fica DENTRO do `BrowserRouter` porque o AuthProvider usa
 * `useNavigate` para reagir ao 401 — fora do router, o hook lança.
 */
export function Router() {
  return (
    <BrowserRouter>
      <Providers>
        <Routes>
          {/* Públicas */}
          <Route path={ROTAS.home} element={<LandingPage />} />
          <Route path={ROTAS.login} element={<LoginPage />} />
          <Route path={ROTAS.registro} element={<RegistroPage />} />
          <Route path={ROTAS.verificarEmail} element={<VerificarEmailPage />} />
          <Route path={ROTAS.esqueciSenha} element={<EsqueciSenhaPage />} />
          <Route path={ROTAS.termosDeUso} element={<TermosDeUsoPage />} />
          <Route path={ROTAS.politicaDePrivacidade} element={<PoliticaDePrivacidadePage />} />
          <Route path={ROTAS.redefinirSenha} element={<RedefinirSenhaPage />} />
          {/* Ponte do app mobile: troca o token de uso único por sessão. */}
          <Route path={ROTAS.entrarPeloApp} element={<EntrarPeloAppPage />} />
          {/* Documento compartilhado. Pública porque quem abre é o tutor, que
              não tem conta — o token da URL é a credencial inteira, e é o
              backend que a valida. Fora do AppLayout de propósito: esta tela
              não é uma porta para dentro do sistema. */}
          <Route path={ROTAS.documentoCompartilhado} element={<DocumentoCompartilhadoPage />} />
          {/* Verificação da receita de controle especial (QR do PDF). Pública:
              quem abre é a farmácia. Fora do AppLayout, como o documento. */}
          <Route path={ROTAS.verificarReceita} element={<VerificarReceitaPage />} />
          {/* Aceite de convite. Pública pelo mesmo motivo do documento: quem
              abre ainda não tem conta — o token da URL é a credencial. Fora do
              AppLayout: é a porta de ENTRADA, não uma tela de dentro. */}
          <Route path={ROTAS.aceitarConvite} element={<AceitarConvitePage />} />

          {/* Privadas, dentro do shell */}
          <Route element={<PrivateRoute />}>
            <Route element={<AppLayout />}>
              <Route path={ROTAS.dashboard} element={<DashboardPage />} />
              <Route path={ROTAS.perfil} element={<PerfilPage />} />
              {/* Volta do Serpro (SerproID). Privada: a troca do `code` precisa
                  da sessão do médico que fez a ida. */}
              <Route path={ROTAS.perfilSerproId} element={<SerproIdRetornoPage />} />

              {/* Administração do próprio ambiente pelo médico (equipe, convites,
                  cargos). Escopo CLINICA — `ambiente.*` está no cargo `medico`. */}
              <Route element={<RotaComPermissao exigidas={['ambiente.visualizar']} />}>
                <Route path={ROTAS.equipe} element={<EquipePage />} />
              </Route>
              {/* Fora do catálogo de permissões, como Dashboard e Perfil
                  (PLANO-MVP §4) — por isso NÃO está dentro de um
                  `RotaComPermissao`. O recorte do Clube é o RANQUE, aplicado
                  dentro da resposta: quem não tem nível vê a tela com os cupons
                  abertos a todos, não um 403. */}
              <Route path={ROTAS.clube} element={<ClubePage />} />
              <Route path={ROTAS.naoAutorizado} element={<Erro403Page />} />

              {/* Clínico. O escopo por tenant é do servidor — nenhuma destas
                  rotas tem parâmetro de ambiente, e não deve ganhar um.

                  O histórico exige a PRÓPRIA chave: ele agrega prontuário,
                  exame, requisição e receita, e quem só pode ver a ficha
                  cadastral não vê, por isso, o dossiê inteiro. */}
              <Route element={<RotaComPermissao exigidas={['tutores.visualizar']} />}>
                <Route path={ROTAS.tutores} element={<TutoresPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['pacientes.visualizar']} />}>
                <Route path={ROTAS.pacientes} element={<PacientesPage />} />
                {/* Ficha clínica: diagnósticos e peso. Fica sob
                    `pacientes.visualizar`, e não sob `diagnosticos.visualizar`,
                    porque a aba de peso é do paciente — quem não tem a chave de
                    diagnóstico entra e vê só o que lhe cabe, em vez de tomar um
                    403 na tela inteira. O recorte fino é dentro de cada bloco. */}
                <Route path={ROTAS.fichaClinicaPaciente} element={<FichaClinicaPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['exames.visualizar']} />}>
                <Route path={ROTAS.exames} element={<ExamesPage />} />
                <Route path={ROTAS.exameDetalhe} element={<LaudoDoExamePage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['requisicoes_exames.visualizar']} />}>
                <Route path={ROTAS.requisicoes} element={<RequisicoesPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['receituarios.visualizar']} />}>
                <Route path={ROTAS.receituarios} element={<ReceituariosPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['historico.visualizar']} />}>
                <Route path={ROTAS.historicoPaciente} element={<HistoricoPage />} />
              </Route>

              {/* Análise comparativa. Chave PRÓPRIA, e não `exames.visualizar`:
                  quem lê o laudo de um paciente não necessariamente deve ver a
                  população inteira do ambiente agregada. */}
              <Route element={<RotaComPermissao exigidas={['analises.visualizar']} />}>
                <Route path={ROTAS.analises} element={<AnaliseComparativaPage />} />
              </Route>

              {/* Vitrine de parceiros — o médico. */}
              <Route element={<RotaComPermissao exigidas={['parceiros.visualizar']} />}>
                <Route path={ROTAS.parceiros} element={<ParceirosPage />} />
                <Route path={ROTAS.parceiroDetalhe} element={<ParceiroDetalhePage />} />
              </Route>

              {/* Discussões técnicas — compartilhado com o advisor. `criar` é
                  quem pode abrir uma discussão nova; ver é quem já participa
                  de alguma (médico ou advisor). */}
              <Route element={<RotaComPermissao exigidas={['discussoes.visualizar']} />}>
                <Route path={ROTAS.discussoes} element={<DiscussoesPage />} />
                <Route path={ROTAS.discussaoDetalhe} element={<DiscussaoDetalhePage />} />
              </Route>
              <Route element={<RotaComPermissao exigidas={['discussoes.criar']} />}>
                <Route path={ROTAS.discussaoNova} element={<NovaDiscussaoPage />} />
              </Route>

              {/* Lado do advisor: o que ele já está atendendo + saldo de vagas. */}
              <Route element={<RotaComPermissao exigidas={['assessoria.visualizar']} />}>
                <Route path={ROTAS.atendimentos} element={<AtendimentosPage />} />
              </Route>
              <Route element={<RotaComPermissao exigidas={['assessoria.atender']} />}>
                <Route path={ROTAS.meusArtigos} element={<MeusArtigosPage />} />
              </Route>

              {/* Suporte — chamados abertos pelo próprio usuário. A tela de
                  atendimento é outra rota, em Administração, com chave
                  própria: o que o solicitante vê e o que o atendente vê não
                  têm interseção. */}
              <Route element={<RotaComPermissao exigidas={['suporte.visualizar']} />}>
                <Route path={ROTAS.suporte} element={<MeusChamadosPage />} />
                <Route path={ROTAS.suporteDetalhe} element={<ChamadoDetalhePage />} />
              </Route>

              {/* Portal do parceiro — o outro lado da mesma feature. */}
              <Route element={<RotaComPermissao exigidas={['materiais.visualizar']} />}>
                <Route path={ROTAS.portalMateriais} element={<MeusMateriaisPage />} />
              </Route>

              {/* Administração. Cada área exige sua chave; sem ela, o
                  RotaComPermissao renderiza o 403 COM sidebar e header —
                  não redireciona para o login, que seria a mensagem errada. */}
              <Route element={<RotaComPermissao exigidas={['admin_usuarios.visualizar']} />}>
                <Route path={ROTAS.adminUsuarios} element={<UsuariosPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_cargos.visualizar']} />}>
                <Route path={ROTAS.adminCargos} element={<CargosPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_tenants.visualizar']} />}>
                <Route path={ROTAS.adminAmbientes} element={<AmbientesPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_catalogo_exames.visualizar']} />}>
                <Route path={ROTAS.adminCatalogoExames} element={<CatalogoExamesPage />} />
                {/* Antes de `:id`, senão "pendencias" é lido como id de exame. */}
                <Route
                  path={ROTAS.adminCatalogoPendencias}
                  element={<PendenciasLaboratorioPage />}
                />
                <Route path={ROTAS.adminCatalogoExameDetalhe} element={<ExameDetalhePage />} />
              </Route>

              <Route
                element={<RotaComPermissao exigidas={['admin_catalogo_farmacos.visualizar']} />}
              >
                <Route path={ROTAS.adminCatalogoFarmacos} element={<CatalogoFarmacosPage />} />
                <Route path={ROTAS.adminCatalogoFarmacoDetalhe} element={<FarmacoDetalhePage />} />
              </Route>

              <Route
                element={<RotaComPermissao exigidas={['admin_catalogo_diagnosticos.visualizar']} />}
              >
                <Route
                  path={ROTAS.adminCatalogoDiagnosticos}
                  element={<CatalogoDiagnosticosPage />}
                />
              </Route>

              <Route
                element={
                  <RotaComPermissao
                    exigidas={['admin_cupons.visualizar', 'admin_cupons_discussao.visualizar']}
                  />
                }
              >
                <Route path={ROTAS.adminCupons} element={<CuponsPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_unidades.visualizar']} />}>
                <Route path={ROTAS.adminUnidades} element={<UnidadesPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_parceiros.visualizar']} />}>
                <Route path={ROTAS.adminParceiros} element={<ParceirosAdminPage />} />
              </Route>

              <Route
                element={<RotaComPermissao exigidas={['admin_produtos_parceiros.visualizar']} />}
              >
                <Route path={ROTAS.adminProdutosParceiros} element={<ProdutosParceirosPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_materiais.visualizar']} />}>
                <Route path={ROTAS.adminMateriais} element={<AprovacaoMateriaisPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_metricas.visualizar']} />}>
                <Route path={ROTAS.adminMetricasParceiros} element={<MetricasParceirosPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_auditoria.visualizar']} />}>
                <Route path={ROTAS.adminAuditoria} element={<AuditoriaPage />} />
              </Route>

              {/* As duas rotas na MESMA tela: `:id` é o link do e-mail de alerta
                  crítico e abre a lista já com o detalhe daquele alerta. */}
              <Route element={<RotaComPermissao exigidas={['admin_alertas.visualizar']} />}>
                <Route path={ROTAS.adminAlertas} element={<AlertasPage />} />
                <Route path={ROTAS.adminAlertaDetalhe} element={<AlertasPage />} />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_relatorios.visualizar']} />}>
                <Route path={ROTAS.adminRelatorios} element={<RelatoriosPage />} />
              </Route>

              <Route
                element={<RotaComPermissao exigidas={['admin_precos_discussao.visualizar']} />}
              >
                <Route path={ROTAS.adminPrecosDiscussao} element={<PrecosDiscussaoPage />} />
              </Route>

              <Route
                element={<RotaComPermissao exigidas={['admin_especialidades_discussao.visualizar']} />}
              >
                <Route
                  path={ROTAS.adminEspecialidadesDiscussao}
                  element={<EspecialidadesDiscussaoPage />}
                />
              </Route>

              <Route element={<RotaComPermissao exigidas={['admin_advisors.visualizar']} />}>
                <Route path={ROTAS.adminAdvisors} element={<AdvisorsPage />} />
              </Route>

              {/* Fila de suporte — CROSS-TENANT: alcança chamado de qualquer
                  ambiente. Quem dá esse alcance é `admin_suporte.*`, nunca o
                  escopo de tenant (ver `chamados-admin.service.ts`). */}
              <Route element={<RotaComPermissao exigidas={['admin_suporte.visualizar']} />}>
                <Route path={ROTAS.adminSuporte} element={<FilaDeChamadosPage />} />
                <Route path={ROTAS.adminSuporteDetalhe} element={<ChamadoAtendimentoPage />} />
              </Route>
            </Route>
          </Route>

          {/* Rota `*` explícita: a V1 não tinha, e URL errada caía no Dashboard
              em silêncio — escondendo link quebrado de todo mundo. */}
          <Route path="*" element={<Erro404Page />} />
        </Routes>
      </Providers>
    </BrowserRouter>
  );
}
